import { useMemo, useState, useCallback } from "react";
import {
  Box,
  Text,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  HStack,
  Badge,
  VStack,
  Checkbox,
  IconButton,
  useUpdateEffect,
  CircularProgress,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { motion, useAnimation } from "framer-motion";

import Select from "components/Select/Select";
import Button from "components/Button/Button";
import SuccessModal from "components/Modals/Success";
import SwapPreview from "components/Modals/swap/SwapPreview";
import { NumberInputWithMax as NumberInput } from "components/Input/NumberInput";

import { ReactComponent as SwapIcon } from "assets/svg/swap.svg";
import { ReactComponent as WarnIcon } from "assets/icons/warn.svg";

import useWallet from "hooks/useWalletBalance";
import {
  useGetPairsQuery,
  useGetSwapRateQuery,
  useSwapMutation,
} from "services/swapApi";

import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import getError from "util/getError";
import toPrecision from "util/toPrecision";
import toCoinLocale from "util/toCoinLocale";
import constants, { isProd } from "config/constant";
import { useGetMinimumWithdrawalAmountQuery } from "services/walletApi";

const Label = ({ children, ...xprops }) => (
  <FormLabel fontSize="sm" fontWeight="400" color="#4E4B66" {...xprops}>
    {children}
  </FormLabel>
);

const Info = ({ label, description }) => (
  <HStack fontSize="sm" fontWeight="500">
    <HStack>
      <Box boxSize="18px" as={WarnIcon} />
      <Text color="#2F80ED">{label}</Text>
    </HStack>

    <Badge colorScheme="green">{description}</Badge>
  </HStack>
);

export default function NewPanel() {
  const toast = useToast();
  const controls = useAnimation();
  const { getBalance, cryptos, isLoading } = useWallet();

  const initialState = {
    fromCoin: isProd ? "btc" : "tbtc",
    toCoin: "usdt",
    rate: 0,
  };

  const {
    isOpen: isPreviewOpen,
    onClose: onPreviewClose,
    onOpen: onPreviewOpen,
  } = useDisclosure();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const [state, _setState] = useState(initialState);

  const setState = useCallback(
    (newState) => _setState(() => ({ ...state, ...newState })),
    [state]
  );

  const [
    _swapPair,
    { data: swapData, isLoading: isSwaping, error, isSuccess, isError },
  ] = useSwapMutation();

  const { data: minimumWithdrawData, isLoading: isLoadingMinimum } =
    useGetMinimumWithdrawalAmountQuery(state?.fromCoin, {
      skip: !state?.fromCoin,
    });

  const minimumWithdraw = useMemo(() => {
    // if (state?.coin === "usdt" && state?.network !== "tron") return 50;
    // return constants.coins[coin]?.wallet?.withdraw?.minimum;
    return minimumWithdrawData?.min;
  }, [minimumWithdrawData]);

  const isLessThanMinimum = useMemo(
    () => state?.fromValue && Number(state?.fromValue) < +minimumWithdraw,
    [state?.fromValue, minimumWithdraw]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onPreviewOpen();
  };

  const swapPair = () => {
    const reqBody = {
      swapRate: state?.rate,
      destToken: state?.toCoin,
      sourceToken: state?.fromCoin,
      sourceAmount: state?.fromValue,
      destAmount: state?.toValue,
    };

    console.log("REQ DATA", reqBody);

    _swapPair(reqBody);
  };

  const handleSuccessModalClose = () => {
    onClose();
    _setState(initialState);
  };

  const coinMap = useCallback((token) => {
    const map = {
      [token]: token,
      tbtc: "btc",
      teth: "eth",
    };

    return map[token ?? "tbtc"];
  }, []);

  const { data: pairData, isFetching: isGettingPairs } = useGetPairsQuery(
    coinMap(state?.fromCoin),
    { skip: !state?.fromCoin }
  );

  const { data: rateData, isFetching: isRating } = useGetSwapRateQuery(
    { base: coinMap(state?.fromCoin), sub: coinMap(state?.toCoin) },
    { skip: !state?.fromCoin && !state?.toCoin }
  );

  const swapInputs = useCallback(() => {
    setState({
      fromCoin: state?.toCoin,
      toCoin: state?.fromCoin,
      animate: !state?.animate,
    });
    controls.set({
      rotate: "180deg",
      //   transition: { duration: 3 },
    });
  }, [setState, state, controls]);

  const swapables = useMemo(
    () =>
      uniqBy(cryptos, "coin")
        .map((crypto) => crypto.coin)
        .filter((coin) => coin !== "usdt-tron"),
    [cryptos]
  );

  const pairs = useMemo(
    () => uniq(pairData?.pairs ?? []).filter((pair) => pair !== "usdt-tron"),
    [pairData]
  );

  const rate = useMemo(() => rateData?.rate, [rateData]);

  const balance = useMemo(
    () => getBalance(state?.fromCoin),
    [getBalance, state?.fromCoin]
  );

  const isInsufficient = useMemo(
    () => +state?.fromValue > +balance,
    [state?.fromValue, balance]
  );

  const swapFee = useMemo(
    () => +state?.toValue * (constants.swap.fee / 100),
    [state?.toValue]
  );

  const isDisabled = useMemo(
    () =>
      !(
        state?.fromValue &&
        state?.toValue &&
        state?.fromCoin &&
        state?.toCoin &&
        state?.agreed
      ) ||
      isRating ||
      isGettingPairs ||
      isInsufficient ||
      isLessThanMinimum ||
      isSwaping,
    [
      state,
      isRating,
      isGettingPairs,
      isInsufficient,
      isSwaping,
      isLessThanMinimum,
    ]
  );

  useUpdateEffect(() => {
    if (rate && state?.fromValue && state?.changeRef === "from") {
      // setState({ rate, toValue: +state?.fromValue / rate });

      if (constants.stableCoins.includes(state?.fromCoin))
        setState({ rate, toValue: +state?.fromValue / rate });
      if (!constants.stableCoins.includes(state?.fromCoin))
        setState({ rate, toValue: +state?.fromValue * rate });
    }
    if (rate && state?.toValue && state?.changeRef === "to") {
      // setState({ rate, fromValue: +state?.toValue * rate });
      if (constants.stableCoins.includes(state?.fromCoin))
        setState({ rate, fromValue: +state?.toValue * rate });
      if (!constants.stableCoins.includes(state?.fromCoin))
        setState({ rate, fromValue: +state?.toValue / rate });
    }
  }, [rate, state?.fromValue, state?.toValue, state?.changeRef]);

  useUpdateEffect(() => {
    if (isSuccess && swapData) {
      onOpen();
      onPreviewClose();
    }

    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Error",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [isSuccess, error, swapData, isError, onOpen, toast, onPreviewClose]);

  console.log("STATE", state, pairs, rate);

  return (
    <>
      {isLoading && (
        <VStack my="40px">
          <CircularProgress
            ml="20px !important"
            isIndeterminate
            color="var(--primary)"
            size="20px"
          />
        </VStack>
      )}

      {!isLoading && (
        <Stack as="form" mt="20px" onSubmit={handleSubmit}>
          <FormControl mb="28px !important" w="100%">
            <HStack mb="8px" justifyContent="space-between">
              <Label mb="0 !important">You send</Label>

              {state?.fromCoin && !isNaN(balance) && (
                <HStack alignItems="flex-end">
                  <Label mb="0 !important" mr="4px !important" fontWeight="500">
                    Available Balance:
                  </Label>
                  <Label
                    color="var(--primary)"
                    mb="0 !important"
                    ml="0 !important"
                  >
                    {toPrecision(balance, state?.fromCoin)}
                  </Label>
                </HStack>
              )}
            </HStack>

            <HStack alignItems="flex-start">
              <Select
                _container={{ flex: ".5" }}
                shadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
                value={state?.fromCoin ?? ""}
                placeholder="Select Coin"
                onChange={(e) => setState({ fromCoin: e.target.value })}
              >
                {swapables?.map((coin) => (
                  <option key={`swapable-${coin}`} value={coin}>
                    {toCoinLocale(coin)}
                  </option>
                ))}
              </Select>

              <Stack
                flex=".5"
                ml="10px !important"
                //   shadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
                alignItems="flex-end"
              >
                <NumberInput
                  isInvalid={isInsufficient || isLessThanMinimum}
                  _container={{
                    w: "100%",
                    //     flex: ".5",
                    //     ml: "10px !important",
                    shadow: "0px 0px 5px rgba(0, 0, 0, 0.05)",
                  }}
                  value={state?.fromValue ?? ""}
                  onChange={(val) =>
                    setState({ fromValue: val, changeRef: "from" })
                  }
                  onMax={() =>
                    setState({
                      fromValue: toPrecision(
                        balance - 0.01,
                        state?.fromCoin,
                        false
                      ),
                    })
                  }
                />
                {isInsufficient && (
                  <FormHelperText color="red.400">
                    Insufficient Balance
                  </FormHelperText>
                )}

                {!isLoadingMinimum && minimumWithdraw && (
                  <FormHelperText
                    as={HStack}
                    color={isLessThanMinimum ? "red.400" : "initial"}
                  >
                    <Text>
                      Minimum {toCoinLocale(state?.fromCoin)} to swap:
                    </Text>
                    <Text color="var(--primary)">
                      {toPrecision(minimumWithdraw, state?.fromCoin)}
                    </Text>
                  </FormHelperText>
                )}
              </Stack>
            </HStack>
          </FormControl>

          <VStack>
            <motion.div animate={controls}>
              <IconButton
                aria-label="swap token"
                icon={<Box as={SwapIcon} />}
                size="sm"
                _hover={{ bg: "var(--primary)", color: "white" }}
                _active={{
                  color: "var(--primary)",
                  bg: "white",
                  shadow: "0 0 0 3px var(--focusColor)",
                }}
                _focus={{ shadow: "0 0 0 3px var(--focusColor)" }}
                onClick={swapInputs}
              />
            </motion.div>
          </VStack>

          <FormControl w="100%">
            <HStack mb="8px">
              <Label mb="0 !important" mr="0 !important">
                You get approximately
              </Label>
              {(isGettingPairs || isRating) && (
                <CircularProgress
                  size="14px"
                  color="green.400"
                  isIndeterminate
                />
              )}
            </HStack>

            <HStack>
              <Select
                _container={{
                  flex: ".5",
                }}
                shadow="0px 0px 5px rgba(0, 0, 0, 0.05)"
                value={state?.toCoin ?? ""}
                onChange={(e) => setState({ toCoin: e.target.value })}
                placeholder="Select Coin"
              >
                {pairs?.map((coin) => (
                  <option key={`pair-${coin}`} value={coin}>
                    {toCoinLocale(coin)}
                  </option>
                ))}
              </Select>

              <NumberInput
                _container={{
                  flex: ".5",
                  ml: "10px !important",
                  shadow: "0px 0px 5px rgba(0, 0, 0, 0.05)",
                }}
                value={state?.toValue ?? ""}
                onChange={(val) => setState({ toValue: val, changeRef: "to" })}
              />
            </HStack>
          </FormControl>

          <Stack mt="48px !important">
            {/* <Info label="Network fee" description="0.00801861 ETH" /> */}
            <Info label="Estimated arrival" description="Instant" />
            {!isNaN(swapFee) && (
              <Info
                label="Swap fee 0.15%"
                description={toPrecision(swapFee ?? 0, state?.toCoin)}
              />
            )}

            <Checkbox
              mt="32px !important"
              size="lg"
              colorScheme="green"
              isChecked={state?.agreed ?? false}
              onChange={(e) => setState({ agreed: e.target.checked })}
              sx={{
                ".chakra-checkbox__control": {
                  borderRadius: "50%",
                  _focus: {
                    shadow: "0 0 0 3px var(--focusColor)",
                  },
                },
                ".chakra-checkbox__control[data-checked]": {
                  bg: "var(--primary)",
                },
                ".chakra-checkbox__label": {
                  fontSize: "sm",
                },
              }}
            >
              I agree with Terms of Use, Privacy Policy and AML/KYC
            </Checkbox>
          </Stack>

          <Button
            mt="34px !important"
            maxW="280px"
            minH="64px"
            type="submit"
            disabled={isDisabled}
          >
            Swap
          </Button>
        </Stack>
      )}

      <SwapPreview
        isOpen={isPreviewOpen}
        isLoading={isSwaping}
        onClose={onPreviewClose}
        onConfirm={swapPair}
        title="Summary"
        data={{
          [`${toCoinLocale(state?.fromCoin)} Quantity`]: toPrecision(
            state?.fromValue,
            state?.fromCoin
          ),
          [`${toCoinLocale(state?.toCoin)} Quantity`]: toPrecision(
            state?.toValue,
            state?.toCoin
          ),
          [`Fees (${constants.swap.fee}%)`]: toPrecision(
            swapFee ?? 0,
            state?.toCoin
          ),
          "Final Quatity": toPrecision(
            (+state?.toValue ?? 0) - (+swapFee ?? 0),
            state?.toCoin
          ),
        }}
      />

      <SuccessModal
        isOpen={isOpen}
        onClose={handleSuccessModalClose}
        title="Success"
        message="Swap Successful!"
      />
    </>
  );
}
